/**
 * Client-side dedupe windows for measurement candidates. The server applies its own dedupe keys
 * (docs/07 section 5); this only avoids sending obvious repeats from one browser.
 */
export interface DedupeStore {
  load(): Record<string, number> | null;
  save(map: Record<string, number>): void;
}

export class WindowDedupe {
  private lastSent = new Map<string, number>();

  constructor(
    readonly windowMs: number,
    private readonly store?: DedupeStore,
  ) {
    const loaded = store?.load();
    if (loaded) {
      for (const [k, v] of Object.entries(loaded)) if (typeof v === "number" && Number.isFinite(v)) this.lastSent.set(k, v);
    }
  }

  /** True (and records the send) when `key` has not been sent within the window. */
  allow(key: string, now: number): boolean {
    const last = this.lastSent.get(key);
    if (last !== undefined && now - last < this.windowMs && now >= last) return false;
    this.lastSent.set(key, now);
    this.persist(now);
    return true;
  }

  has(key: string, now: number): boolean {
    const last = this.lastSent.get(key);
    return last !== undefined && now - last < this.windowMs && now >= last;
  }

  prune(now: number): void {
    for (const [k, v] of this.lastSent) if (now - v >= this.windowMs) this.lastSent.delete(k);
  }

  private persist(now: number): void {
    if (!this.store) return;
    this.prune(now);
    this.store.save(Object.fromEntries(this.lastSent));
  }
}

export const SIGN_DEDUPE_WINDOW_MS = 30 * 60 * 1000;
export const PANEL_DEDUPE_WINDOW_MS = 30 * 1000;
